import { inject, singleton } from 'tsyringe';
import { LoggerService } from 'app-core/src/services/logger.service';
import {
  kNameOfTesterWindowService,
  ITesterWindowService,
} from 'app-core/src/interfaces/tester-window-service.interface';
import { Application } from 'app-core/src/models/application.model';
import { Game } from 'app-core/src/models/game.model';

// -----------------------------------------------------------------------------
@singleton()
export class TesterController {
  private readonly logger = LoggerService.getCategory('TesterController');
  private selectedGame: Game;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kNameOfTesterWindowService)
    private readonly testerWindowService: ITesterWindowService,
  ) {}

  // ---------------------------------------------------------------------------
  public async run() {
    try {
      this.createTesterForm();
    } catch (ex) {
      this.logger.error('Error:', ex);
    }
  }

  // ---------------------------------------------------------------------------
  private async createTesterForm() {
    const form = document.getElementById('tester-form');

    const envInput: HTMLElement = document.querySelector('#environment');
    envInput.onchange = this.handleEnvChange;

    this.AddActionButtons();

    await this.loadGamesFromConfig();

    form.addEventListener('submit', async (event: Event) => {
      event.preventDefault();
      if (
        // eslint-disable-next-line dot-notation
        event.currentTarget['appId'].value === '' ||
        // eslint-disable-next-line dot-notation
        event.currentTarget['gameId'].value === ''
      ) {
        console.error('[Notification tester] Missing parameters');
        return;
      }

      // eslint-disable-next-line dot-notation
      const appId = event.currentTarget['appId'].value;
      // eslint-disable-next-line dot-notation

      const app: Application = await this.getApplication(appId);
      // eslint-disable-next-line dot-notation
      const useDevEnv = event.currentTarget['environment'].value;
      this.testerWindowService.transferTesterNotification(
        this.selectedGame,
        app,
        useDevEnv,
      );
    });
  }

  // ---------------------------------------------------------------------------
  private handleGameChange = async (evt) => {
    document.querySelectorAll('.appSelection').forEach((app) => app.remove());
    const game: Game = await this.testerWindowService.getGameConfiguration(
      parseInt(evt.target.value),
    );
    this.selectedGame = game;
    this.loadAppsFromConfig(game);
  };

  // ---------------------------------------------------------------------------
  private handleEnvChange = async (event) => {
    await this.testerWindowService.setEnvironment(event.target.value);
    await this.loadGamesFromConfig();
  };

  // ---------------------------------------------------------------------------
  private loadAppsFromConfig(game: Game) {
    const appInput = document.getElementById('appId');
    document.querySelectorAll('.appSelection').forEach((item) => {
      item.remove();
    });

    game.apps.sort((a, b) => {
      return this.sort(a.name, b.name);
    });

    for (const app of game.apps) {
      const option = document.createElement('option');
      option.setAttribute('value', app.uid);
      option.setAttribute('class', 'appSelection');
      option.textContent = app.name;
      appInput.appendChild(option);
    }
  }

  // ---------------------------------------------------------------------------
  private async loadGamesFromConfig() {
    const gameInput = document.getElementById('gameId');
    gameInput.onchange = this.handleGameChange;
    const games: Game[] =
      await this.testerWindowService.getAllGamesConfigurations();
    document.querySelectorAll('.game').forEach((item) => {
      item.remove();
    });

    games.sort((a, b) => {
      return this.sort(a.name, b.name);
    });

    for (const game of games) {
      const option = document.createElement('option');
      option.setAttribute('value', game.id.toString());
      option.setAttribute('class', 'game');
      option.textContent = game.name;
      gameInput.appendChild(option);
    }

    gameInput.dispatchEvent(new Event('change'));
  }

  // ---------------------------------------------------------------------------
  private async getApplication(appId: any): Promise<Application> {
    const app = this.selectedGame.apps.find((app) => app.uid === appId);
    if (app) {
      return app;
    }

    return {
      displayLimit: null,
      name: null,
      recommendWeight: null,
      uid: appId,
      appConditions: null,
      blacklistExtensionIds: null,
      phasingForElectron: 100,
      phasingForOverwolf: 100,
      gameEventTrigger: null,
      aliasApplicationDetails: null,
    };
  }

  // ---------------------------------------------------------------------------
  private AddActionButtons() {
    this.addClearTimestampBtn();

    this.addClearConditionsSummaryBtn();

    this.addResetLocalStorageBtn();

    this.addResetNOtificationsHistoryBtn();
  }

  // ---------------------------------------------------------------------------
  private addResetNOtificationsHistoryBtn() {
    const clearNotificationHistory = document.getElementById(
      'resetNotificationsHistory',
    );

    clearNotificationHistory.addEventListener('click', () => {
      this.testerWindowService.resetNotificationsHistory();
    });
  }

  // ---------------------------------------------------------------------------
  private addResetLocalStorageBtn() {
    const clearLocalStorageBtn = document.getElementById('resetLocalStorage');

    clearLocalStorageBtn.addEventListener('click', () => {
      this.testerWindowService.resetLocalStorage();
    });
  }

  // ---------------------------------------------------------------------------
  private addClearConditionsSummaryBtn() {
    const clearConditionsSummaryBtn = document.getElementById(
      'resetConditionsSummary',
    );

    clearConditionsSummaryBtn.addEventListener('click', () => {
      this.testerWindowService.resetConditionsSummary();
    });
  }

  // ---------------------------------------------------------------------------
  private addClearTimestampBtn() {
    const clearTimestampBtn = document.getElementById(
      'resetLastNotificationTimestamp',
    );

    clearTimestampBtn.addEventListener('click', () => {
      this.testerWindowService.resetNotificationTimestamp();
    });
  }

  // ---------------------------------------------------------------------------
  private sort(a: string, b: string) {
    if (a > b) {
      return 1;
    }

    if (a < b) {
      return -1;
    }

    return 0;
  }
}
